import React, { createContext, useEffect, useState } from "react";
import type { AuthChangeEvent, Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase/client.ts";

interface UserAuthState {
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  loading: boolean;
}

interface UserAuthContextValue extends UserAuthState {
  signInWithOtp: (email: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

export const UserAuthContext = createContext<UserAuthContextValue | null>(null);

interface UserAuthProviderProps {
  children: React.ReactNode;
}

export function UserAuthProvider({ children }: UserAuthProviderProps) {
  const [state, setState] = useState<UserAuthState>({
    user: null,
    session: null,
    isAuthenticated: false,
    loading: true,
  });

  useEffect(() => {
    const client = supabase;

    if (!client) {
      setState({
        user: null,
        session: null,
        isAuthenticated: false,
        loading: false,
      });
      return;
    }

    let mounted = true;

    const bootstrap = async () => {
      const { data } = await client.auth.getSession();
      if (!mounted) return;
      setState({
        user: data.session?.user ?? null,
        session: data.session,
        isAuthenticated: Boolean(data.session?.user),
        loading: false,
      });
    };

    void bootstrap();

    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((_event: AuthChangeEvent, session: Session | null) => {
      if (!mounted) return;
      setState({
        user: session?.user ?? null,
        session,
        isAuthenticated: Boolean(session?.user),
        loading: false,
      });
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signInWithOtp = async (email: string) => {
    if (!supabase) {
      throw new Error("Supabase is not configured.");
    }

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${globalThis.location.origin}/auth/user/callback`,
      },
    });

    if (error) {
      throw new Error(error.message);
    }
  };

  const signInWithGoogle = async () => {
    if (!supabase) {
      throw new Error("Supabase is not configured.");
    }

    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${globalThis.location.origin}/auth/user/callback`,
      },
    });

    if (error) {
      throw new Error(error.message);
    }
  };

  const signOut = async () => {
    if (!supabase) {
      return;
    }

    const { error } = await supabase.auth.signOut();
    if (error) {
      throw new Error(error.message);
    }
  };

  return (
    <UserAuthContext.Provider
      value={{
        ...state,
        signInWithOtp,
        signInWithGoogle,
        signOut,
      }}
    >
      {children}
    </UserAuthContext.Provider>
  );
}
